import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { Input } from "@/components/lilia-ui/input";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";
import { testIds } from "@/testIds";

type TheoremType = "theorem" | "definition" | "lemma" | "corollary" | "proposition" | "remark" | "example" | "proof";

const THEOREM_TYPES: TheoremType[] = [
  "theorem", "definition", "lemma", "corollary", "proposition", "remark", "example", "proof",
];

const THEOREM_LABELS: Record<TheoremType, string> = {
  theorem: "Theorem", definition: "Definition", lemma: "Lemma", corollary: "Corollary",
  proposition: "Proposition", remark: "Remark", example: "Example", proof: "Proof",
};

const THEOREM_TYPE_OPTIONS = THEOREM_TYPES.map(type => ({ value: type, label: THEOREM_LABELS[type] }));

const THEOREM_STYLES: Record<TheoremType, { hasNumber: boolean; italic: boolean }> = {
  theorem: { hasNumber: true, italic: true },
  definition: { hasNumber: true, italic: false },
  lemma: { hasNumber: true, italic: true },
  corollary: { hasNumber: true, italic: true },
  proposition: { hasNumber: true, italic: true },
  remark: { hasNumber: true, italic: false },
  example: { hasNumber: true, italic: false },
  proof: { hasNumber: false, italic: false },
};

export function TheoremNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawTheoremType = node.attrs.theoremType;
  const theoremType: TheoremType =
    typeof rawTheoremType === "string" && THEOREM_TYPES.includes(rawTheoremType as TheoremType)
      ? (rawTheoremType as TheoremType)
      : "theorem";

  const title = (node.attrs.title as string) || "";
  const text = (node.attrs.text as string) || "";
  const label = (node.attrs.label as string) || "";
  const typeInfo = THEOREM_STYLES[theoremType];

  let headerText = THEOREM_LABELS[theoremType];
  if (title) headerText += ` (${title})`;

  const [isOpen, setIsOpen] = useState(false);
  const [draftType, setDraftType] = useState<TheoremType>(theoremType);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftText, setDraftText] = useState(text);
  const [draftLabel, setDraftLabel] = useState(label);

  const handleOpen = useCallback(() => {
    setDraftType(theoremType);
    setDraftTitle(title);
    setDraftText(text);
    setDraftLabel(label);
    setIsOpen(true);
  }, [theoremType, title, text, label]);

  const handleSave = useCallback(() => {
    updateAttributes({
      theoremType: draftType,
      title: draftTitle,
      text: draftText,
      label: draftLabel,
    });
    setIsOpen(false);
  }, [draftType, draftTitle, draftText, draftLabel, updateAttributes]);

  const draftTypeInfo = THEOREM_STYLES[draftType] || THEOREM_STYLES.theorem;

  return (
    <NodeViewWrapper data-type="theorem-block" data-testid={testIds.theorem.block} data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={`${styles.theoremBlock} ${styles[`theorem-${theoremType}`]}`}
        data-testid={testIds.theorem.preview}
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
        title="Click to edit"
      >
        <div className={styles.theoremHeader}>
          <span className={styles.theoremLabel}>
            <strong>{headerText}</strong>
            {theoremType === "proof" && "."}
          </span>
        </div>
        {text ? (
          <div
            className={`${styles.theoremContent} ${typeInfo.italic ? styles.italic : ""}`}
            dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
          />
        ) : (
          <div className={`${styles.theoremContent} ${typeInfo.italic ? styles.italic : ""}`}>
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add content...</span>
          </div>
        )}
        {theoremType === "proof" && <div className={styles.qed}>&#9633;</div>}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${THEOREM_LABELS[draftType]}`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={720}
      >
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
            <Select value={draftType} onValueChange={(val) => setDraftType((val || "theorem") as TheoremType)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THEOREM_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Title (optional)</label>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="e.g., Fundamental Theorem"
              style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Content</label>
          <textarea
            data-testid={testIds.theorem.editText}
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder={`Enter ${THEOREM_LABELS[draftType].toLowerCase()} content...`}
            rows={8}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", fontStyle: draftTypeInfo.italic ? "italic" : "normal", lineHeight: 1.6,
            }}
          />
        </div>

        {draftTypeInfo.hasNumber && (
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label (for cross-references)</label>
            <Input
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.currentTarget.value)}
              placeholder={`e.g., thm:${draftType}1`}
              className="w-[250px]"
            />
          </div>
        )}
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
